import { db } from "@firebase";
import { COLLECTIONS } from "constant";
import {
  collection,
  addDoc,
  doc,
  setDoc,
  getDocs,
  query,
  where,
  Timestamp,
} from "firebase/firestore";
import { LearningLogType } from "types"; // Import the type for Learning Log

class LearningLogs {
  async createOrUpdateLearningLog(
    userId: string,
    contentId: string,
    sectionId: string,
    contentTitle: string,
    progress: number = 20 // ✅ Default progress 20%
  ) {
    try {
      if (!userId || !contentId || !sectionId) {
        console.error("❌ Error: Missing required data for logging", {
          userId,
          contentId,
          sectionId,
        });
        return;
      }

      const learningLogRef = collection(db, COLLECTIONS.LEARNING_LOGS);

      // Check if a log already exists for this user and content
      const existingLogQuery = query(
        learningLogRef,
        where("userId", "==", userId),
        where("contentId", "==", contentId)
      );

      const existingLogSnapshot = await getDocs(existingLogQuery);

      if (!existingLogSnapshot.empty) {
        // If log exists, update the last accessed date
        const logDoc = existingLogSnapshot.docs[0].ref;
        await setDoc(logDoc, { createdAt: Timestamp.now() }, { merge: true });
        console.log("✅ Learning log updated:", logDoc.id);
      } else {
        // Create a new learning log entry
        const newLogData = {
          userId,
          contentId,
          sectionId,
          contentTitle,
          createdAt: Timestamp.now(),
          progress: progress, // ✅ Start progress at 20%
        };

        const docRef = await addDoc(learningLogRef, newLogData);
        console.log("✅ New learning log created:", docRef.id);
      }
    } catch (error) {
      console.error("❌ Error in creating/updating learning log:", error);
    }
  }

  async getProgress(userId: string, contentId: string) {
    try {
      const learningLogRef = collection(db, COLLECTIONS.LEARNING_LOGS);
      const logQuery = query(
        learningLogRef,
        where("userId", "==", userId),
        where("contentId", "==", contentId)
      );

      const logSnapshot = await getDocs(logQuery);

      if (!logSnapshot.empty) {
        const logData = logSnapshot.docs[0].data();
        return logData.progress || 0; // ✅ Default progress 0
      }
      return 0;
    } catch (error) {
      console.error("❌ Error retrieving progress:", error);
      return 0;
    }
  }

  async getLastAccessedDate(userId: string, contentId: string) {
    try {
      const learningLogRef = collection(db, COLLECTIONS.LEARNING_LOGS); // Ensure correct collection
      const logQuery = query(
        learningLogRef,
        where("userId", "==", userId),
        where("contentId", "==", contentId)
      );

      const logSnapshot = await getDocs(logQuery);

      if (!logSnapshot.empty) {
        const logData = logSnapshot.docs[0].data();
        console.log("📌 Firestore Last Accessed Date:", logData.createdAt);

        // ✅ Ensure `createdAt` is converted to a Date object
        return logData.createdAt.toDate();
      }
      return null;
    } catch (error) {
      console.error("❌ Error retrieving last accessed date:", error);
      return null;
    }
  }

  async getAllLearningLogs(userId: string) {
    try {
      const learningLogRef = collection(db, COLLECTIONS.LEARNING_LOGS);
      const logQuery = query(learningLogRef, where("userId", "==", userId));
      const logSnapshot = await getDocs(logQuery);

      if (!logSnapshot.empty) {
        const logs = logSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        return logs;
      }

      return [];
    } catch (error) {
      console.error("❌ Error retrieving all learning logs:", error);
      return [];
    }
  }
}

const LearningLogService = new LearningLogs();

export { LearningLogService };
